/**
 * Identidad de marca de la PLATAFORMA (el SaaS en sí, no los tenants).
 *
 * Única fuente de verdad: nombre, lettering del logo, colores de identidad y
 * rutas de assets. Cambiar de marca (nombre o colores) = editar SOLO este archivo;
 * todos los componentes de marca (`BrandMark`, `BrandWordmark`, `BrandLockup`),
 * `panelMeta`, el `<title>` del HTML y el favicon leen de aquí.
 *
 * Nota: estos colores son los hex FIJOS de la identidad (logo). No confundir con
 * los tokens themeables `ui-*` del panel — la marca es constante, el tema no.
 */
export const brand = {
  /** Nombre canónico para prosa, títulos y voz del producto. */
  name: 'Destakia',

  /** Lettering del wordmark (logo "con nombre"), en minúscula estilizada. */
  wordmarkText: 'destakia',
  /**
   * Tramo `[inicio, fin)` de `wordmarkText` que va en color de acento y lleva la
   * estrella encima (la "k" de destakia). Si cambias de nombre, ajusta el tramo
   * o ponlo a `null` para un wordmark monocromo sin acento.
   */
  wordmarkAccent: [5, 6] as readonly [number, number] | null,

  tagline: 'Destaca con inteligencia.',
  /** Dominio principal (tentativo hasta cerrar registro). */
  domain: 'destakia.com',

  /** Colores fijos de identidad (≠ tokens `ui-*` themeables del panel). */
  colors: {
    /** Tinta iris — el cuerpo del wordmark en modo claro. */
    ink: '#1E1B38',
    /** Acento ámbar — la "k" y la estrella. */
    accent: '#C2841C',
    /** Crema — fondo de marca. */
    cream: '#FAF6E9',
  },

  /**
   * Tipografía canónica de la marca. Valores del catálogo de `@palacete/shared/branding`
   * (`DisplayFont` / `BodyFont`) para que coincidan con lo que el renderer del sitio sabe servir.
   *
   * IMPORTANTE — esto NO es un override en vivo: el sitio del tenant lee las fuentes desde su
   * `theme_json` en D1 (`apps/site/src/lib/theme.ts`), no desde aquí. Esta constante es la
   * fuente de verdad *documental*; el seed de `destakia` replica estos valores en su `theme_json`.
   * Cambiar la tipografía de marca = editar aquí y re-sembrar (o ajustar Apariencia en el panel).
   */
  fonts: {
    /** Titulares / display. */
    display: 'sora',
    /** Cuerpo / UI. */
    body: 'inter',
  },

  /** Rutas públicas de los assets de marca (en `apps/panel/public`). */
  assets: {
    /** Arte final vectorial del wordmark a color. */
    wordmark: '/brand/destakia-wordmark.svg',
    /** Variante del wordmark para superficies oscuras. */
    wordmarkInverse: '/brand/destakia-wordmark-inverse.svg',
    /** Marca-símbolo (k + destello) vectorial, apta para favicon y QR. */
    mark: '/brand/destakia-mark.svg',
  },
} as const;

export type Brand = typeof brand;
